import StepType from "../flow/enums/stepType.enum"
import { Flow } from "../flow/flow.component"
import { useState, useEffect, useRef, useContext } from "react"
import { MetforminConfirm } from "./metforminConfirm.component"
import { listMembershipTypes } from "../../services/membershipType.service"
import MembershipTypeCode from "../../enums/membershipTypeCode.enum"
import { completeFlow } from "../../services/flow.service"
import { UserContext } from "../../contexts/user.context"
import ProductHelper from "../../helpers/product.helper"
import Gender from "../../enums/gender.enum"
import TestCode from "../../enums/testCode.enum"
import FlowType from "../../enums/flowType.enum"
import MedicalCondition from "../../enums/medicalCondition.enum"
import FieldType from "../../enums/fieldType.enum"
import PatientHelper from "../../helpers/patient.helper"
import Disqualification from "../../enums/disqualification.enum"
import { BiomarkerPopover } from "../biomarkerPopover/biomarkerPopover.component"
import FileTypeCode from "../../enums/fileTypeCode.enum"
import { listResults } from "../../services/result.service"
import { listTests } from "../../services/test.service"

const disqualifyingMedicalConditions = [
  MedicalCondition.CIRROHOSIS,
  MedicalCondition.END_STAGE_LIVER_DISEASE,
  MedicalCondition.CHRONIC_KIDNEY_DISEASE,
  MedicalCondition.END_STAGE_KIDNEY_DISEASE,
  MedicalCondition.HEART_FAILURE,
  MedicalCondition.LACTIC_ACIDOSIS,
]

export const Step = {
  INTRO: 'intro',
  REASON: 'reason',
  GENDER: 'gender',
  DOB: 'dob',
  MEDICAL_CONDITIONS: 'medical-conditions',
  PREGNANCY: 'pregnancy',
  BREASTFEEDING: 'breastfeeding',
  PRIOR_METFORMIN_USE: 'prior-metformin-use',
  ADVERSE_EFFECTS: 'adverse-effects',
  ALCOHOL: 'alcohol',
  TEST: 'test',
  TEST_VALUES: 'test-values',

  // qualified or disqualified
  INSULIN_RESISTANCE_TEST: 'insulin-resistance-test',
  QUALIFIED: 'qualified',
  DISQUALIFIED: 'disqualified',

  // payment steps 
  SELECT_DURATION: 'select-duration',
  PAY: 'pay',
  NAME: 'name',
  CONTACT: 'contact',
  DELIVERY: 'delivery',
  GOV_ID: 'gov-id',
  ADDITIONAL_INFO: 'additional-info',
  PASSWORD: 'password',
  CONFIRM: 'confirm',
}

export const MetforminFlow = () => {
  const [flow, setFlow] = useState()
  const [skeleton, setSkeleton] = useState()
  const [membershipTypes, setMembershipTypes] = useState()
  const [hasPass, setHasPass] = useState()
  const hasCompletedFlowRef = useRef(false)
  const [initialUser, setInitialUser] = useState()
  const { instalabMembership, currentUser } = useContext(UserContext)
  const [hasTest, setHasTest] = useState(false)
  const [disqualifications, setDisqualifications] = useState([])
  
  useEffect(() => {
    const fetchMembershipTypes = async () => {
      const response = await listMembershipTypes({
        filter: {
          code: {
            $in: [
              MembershipTypeCode.METFORMIN_500MG_1MO,
              MembershipTypeCode.METFORMIN_500MG_3MO,
              MembershipTypeCode.METFORMIN_500MG_12MO,
            ]
          }
        }
      })
      setMembershipTypes(response)
    }
  
    fetchMembershipTypes()
  }, [])

  useEffect(() => {
    // don't show add on if has already run apoe
    const fetchHasTest = async () => {
      const tests = await listTests({
        select: '_id',
        filter: { code: { $in: [TestCode.GLUCOSE, TestCode.HBA1C] } }
      });
  
      const results = await listResults({
        select: '_id values',
        filter: { patient: currentUser }
      });
  
      const hasTest = results.some(({ values }) =>
        values.some(value => value.value && tests.some(test => test._id.toString() === value.test.toString()))
      );
  
      setHasTest(hasTest);
    };
  
    fetchHasTest();
  }, [currentUser]);

  useEffect(() => {
    fetchSkeleton()
  }, [membershipTypes, flow, hasPass, initialUser, instalabMembership, currentUser, hasTest])

  const getMembershipType = (code) => {
    if (!code) return null
    return membershipTypes?.find(membershipType => membershipType.code === code)
  }

  const fetchSkeleton = () => {
    if (hasPass === undefined || !initialUser  || !membershipTypes?.length) return

    setSkeleton({

      [Step.INTRO]: {
        type: StepType.STATIC,
        title: `First, see if Metformin is a fit for you (takes just 2 minutes).`,
        description: `We’ll ask a few quick questions about your health and recent lab work. Your answers help us determine right away whether we can prescribe Metformin (500mg) to you.`,
        nextStep: Step.REASON,
        buttonText: "Let's get started!",
      },
      [Step.REASON]: {
        type: StepType.MULTIPLE_SELECT,
        nextStep: Step.DOB,
        buildUser: true,
        title: `Why are you interested in Metformin?`,
        description: 'Select all that apply.',
        field: {
          name: 'metforminReason',
          required: true,
          options: [ 
          {
            label: `No doctor to help manage my insulin resistance`,
            value: `No doctor to help manage my insulin resistance`,
          }, 
          {
            label: `Lower my risk of diabetes`,
            value: `Lower my risk of diabetes`,
          }, 
          {
            label: "Have family history of diabetes",
            value: "Have family history of diabetes",
          },
          {
            label: "Complement existing lifestyle changes",
            value: "Complement existing lifestyle changes",
          },
          {
            label: "To help me lose weight",
            value: "To help me lose weight",
          },
     
          {
            label: 'Other',
            value: 'Other',
          }]
        },
      },
      [Step.DOB]: {
        type: StepType.SINGLE_INPUT,
        nextStep: Step.GENDER,
        buildUser: true,
        title: `What's your date of birth?`,
        description: `Enter in the format of MM/DD/YYYY.`,
        field: {
          name: 'dob',
          placeholder: 'MM/DD/YYYY',
          date: true,
          inputMode: 'numeric',
          required: true,
        },
        skipIf: () => {
          return initialUser?.dob
        }
      },

      [Step.GENDER]: {
        type: StepType.SINGLE_SELECT,
        nextStep: Step.MEDICAL_CONDITIONS,
        buildUser: true,
        title: `What's your biological sex?`,
        field: {
          name: 'gender',
          required: true,
          options: [{
            label: 'Male',
            value: Gender.MALE,
          }, {
            label: 'Female',
            value: Gender.FEMALE,
          }]
        },
        skipIf: () => {
          return initialUser?.gender
        }
      },

      [Step.MEDICAL_CONDITIONS]: {
        type: StepType.MULTIPLE_SELECT,
        onNextStep: (patient) => {
          if (patient?.gender === Gender.FEMALE) {
            return Step.PREGNANCY
          } else {
            return Step.PRIOR_METFORMIN_USE
          }
        },
        buildUser: true,
        title: `Have you been diagnosed with any of the following conditions?`,
        description: 'Select all that apply.',
        field: {
          name: 'currentMedicalConditions',
          options: disqualifyingMedicalConditions.map(condition => ({
            label: condition,
            value: condition,
          })),
        },
      },

      [Step.PREGNANCY]: {
        type: StepType.SINGLE_SELECT,
        onNextStep: (patient) => {
          if (patient?.isPregnant) {
            return Step.PRIOR_METFORMIN_USE
          } else {
            return Step.BREASTFEEDING
          }
        },
        buildUser: true,
        title: `Are you pregnant or planning to be in the next 12 months?`,
        field: {
          name: 'isPregnant',
          options: [{
            label: 'Yes',
            value: true,
          }, {
            label: 'No',
            value: false,
          }]
        },
      },
      [Step.BREASTFEEDING]: {
        type: StepType.SINGLE_SELECT,
        nextStep: Step.PRIOR_METFORMIN_USE,
        buildUser: true,
        title: `Are you currently breastfeeding?`,
        field: {
          name: 'isBreastfeeding',
          options: [{
            label: 'Yes',
            value: true,
          }, {
            label: 'No',
            value: false,
          }]
        },
      },

      [Step.PRIOR_METFORMIN_USE]: {
        type: StepType.SINGLE_SELECT,
        onNextStep: (patient) => {
          if (patient?.hasPriorMetforminUse) {
            return Step.ADVERSE_EFFECTS
          } else {
            return Step.ALCOHOL
          }
        },
        buildUser: true,
        title: `Have you taken metformin before?`,
        field: {  
          name: 'hasPriorMetforminUse',
          options: [{
            label: 'Yes',
            value: true,
          }, {
            label: 'No',
            value: false,
          }]
        },
      },

      [Step.ADVERSE_EFFECTS]: {
        type: StepType.SINGLE_SELECT,
        nextStep: Step.ALCOHOL,
        buildUser: true,
        title: `Did you have any adverse reactions to metformin before?`,
        field: {  
          name: 'cholesterolAdverseReaction.metformin',
          options: [{
            label: 'Yes',
            value: true,
          }, {
            label: 'No',
            value: false,
          }]
        }
      },

      [Step.ALCOHOL]: {
        type: StepType.SINGLE_INPUT,
        buildUser: true,
        nextStep: Step.TEST,
        title: `How many alcoholic drinks do you usually consume per week?`,
        description: `1 alcoholic drink is equivalent to 1 medium glass of wine, 1 can of regular strength beer, or 1 shot of liquor.`,
        field: {
          name: 'alcoholQty',
          placeholder: 'Type quantity here ...',
        },
      },

      [Step.TEST]: {
        type: StepType.SINGLE_SELECT,
        buildUser: true,
        title: <>Have you had blood work done in the past year that includes <u>fasting glucose</u> or <u>HbA1c</u>?</>,
        field: {
          name: 'hasMetforminTest',
          required: true,
          options: [{
            label: 'Yes',
            value: true,
          }, {
            label: 'No',
            value: false,
          }]
        },
        onNextStep: (patient) => {
          if (patient?.hasMetforminTest || hasTest) {
            return Step.TEST_VALUES
          } 
          else  {
            return Step.INSULIN_RESISTANCE_TEST
          }
        },
        skipIf: () => hasTest
      },

      [Step.TEST_VALUES]: {
        type: StepType.MULTIPLE_INPUT,
        onNextStep: (patient) => {

          const result = PatientHelper.metforminQualified(patient)
          const { qualified, disqualifications } = result
          
          if (qualified) {
            return Step.QUALIFIED
          }
          else {
            setDisqualifications(disqualifications)
            return Step.DISQUALIFIED
          }
        },        
        buildUser: true,
        title: `Enter your latest fasting glucose or HbA1c result.`,
        requiresOneField: true,
        fields: [{
          name: 'glucose',
          label: 'Fasting Glucose',
          number: true,
          inputMode: 'decimal',
          placeholder: '100',
          suffixField: {
            name: 'glucoseUnit',
            default: 'mg/dL',
            options: [{
              label: 'mg/dL',
              value: 'mg/dL',
            }, {
              label: 'mmol/L',
              value: 'mmol/L',
            }]
          }
        }, {
          name: 'hba1c',
          label: 'HbA1c',
          number: true,
          inputMode: 'decimal',
          placeholder: '5.7',
          suffixField: {
            name: 'hba1cUnit',
            default: '%',
            options: [{
              label: '%',
              value: '%',
            }]
          }
        }],
        skipIf: () => hasTest
      },

      [Step.INSULIN_RESISTANCE_TEST]: {
        type: StepType.STATIC,
        title: `Please, first take a blood test to get prescription.`,
        description: <>Currently, we lack the required medical data to proceed. You'll need to get a blood test for fasting glucose or HbA1c to move forward. We can help you with this with our <a href="/test/insulin-resistance-panel">Insulin Resistance Panel</a>. Click the button below to get started.</>,
        buttonText: 'Get Tested',
        path: '/test/insulin-resistance-panel',
      },


      [Step.QUALIFIED]: {
        type: StepType.STATIC,
        title: `You’re approved for Metformin (500 mg)!`,
        description: `Just a few quick questions left before we can send your prescription.`,
        buttonText: 'Continue',
        nextStep: Step.SELECT_DURATION,
      },

      [Step.DISQUALIFIED]: {
        type: StepType.STATIC,
        title: `Apologies, we can't prescribe Metformin (500 mg) to you at this time.`,
        description: () => {

          let text = ''
          switch (disqualifications?.[0]) {
            case Disqualification.PREGNANT:
              text = <>
                <p>While metformin is sometimes prescribed during pregnancy, particularly for women with gestational diabetes or PCOS, it requires close physician monitoring. At Instalab, we do not prescribe metformin during pregnancy because it’s a time when medication needs, safety thresholds, and metabolic demands shift rapidly.</p>
                <p>Even though studies show that metformin is generally well tolerated in pregnancy, we believe this decision is best made with a physician who can provide ongoing, individualized prenatal care. If you’re currently pregnant or planning to become pregnant, we recommend speaking with your OB-GYN or maternal-fetal medicine specialist before starting or continuing metformin.</p>
              </>
              break
            case Disqualification.BREASTFEEDING:
              text = <>
                <p>Metformin does pass into breastmilk in very small amounts, and studies show that most breastfed infants do well, with no signs of low blood sugar or developmental concerns. However, because newborns, especially premature or medically fragile infants, may be more sensitive to medication exposure, we take a cautious approach.</p>
                <p>If you’re nursing and considering metformin, we recommend discussing it with your pediatrician or OB-GYN to make sure it’s appropriate for both you and your baby.</p>
                <p>Once breastfeeding is complete, or if you’re cleared by your doctor, we’d be happy to help you start or restart metformin safely.</p>
              </>
              break
            case Disqualification.METFORMIN_ADVERSE_REACTION:
              text = <>
                <p>Since you’ve had a serious reaction to metformin before, we need to be extra cautious. You may benefit from adjusted timing, or an alternative glucose-lowering medication altogether. </p>
                <p>Our physician-guided <a href="/heart">Heart Health Program</a> is designed for situations just like this. We’ll closely review your history, monitor you carefully, and tailor a treatment plan that’s safe, effective, and built around your individual needs. You won’t have to navigate it alone; we’ll guide you every step of the way.</p>
              </>
              break
            case Disqualification.CHRONIC_KIDNEY_DISEASE:
            case Disqualification.END_STAGE_KIDNEY_DISEASE:
              text = <>
                <p>If your eGFR is below 45 mL/min/1.73 m², the body can’t effectively clear metformin, increasing the risk of lactic acidosis, a rare but serious complication. For your safety, we recommend working with a specialist who can closely monitor your kidney and metabolic health.</p>
                <p>However, if your eGFR is higher than 45 mL/min/1.73 m², we may be able to help you with managing insulin resistance through a personalized approach. Please consider our <a href="/heart">Heart Health Program</a>, we offer a comprehensive review of your condition and create a plan that works for your kidney health and cardiometabolic needs. Our physician-led team will help you explore alternatives that are both effective and safe for you.</p>
              </>
              break
            case Disqualification.END_STAGE_LIVER_DISEASE:
            case Disqualification.CIRROHOSIS:
              text = <>
                <p>We do not offer metformin to individuals with active hepatitis, cirrhosis, or significant liver enzyme elevations. The liver plays a key role in processing lactate, and compromised liver function can increase the risk of dangerous lactate buildup. If you have a history of liver disease, we suggest consulting with your hepatologist before starting any glucose-lowering medication.
                </p>
              </>
              break
            case Disqualification.LACTIC_ACIDOSIS:
              text = <>
                <p>If you’ve experienced lactic acidosis in the past, we cannot safely prescribe metformin. This condition is rare, but potentially fatal, and a history of it indicates heightened sensitivity to medications that affect mitochondrial energy metabolism. Please discuss alternatives with your primary care physician or endocrinologist.</p>
              </>
              break
            case Disqualification.HEAVY_ALCOHOL_CONSUMPTION:
              text = <>
                <p>Metformin is not recommended for people who engage in heavy drinking or daily alcohol use above clinical safety thresholds (more than 14 drinks/week, or regular binge drinking). Alcohol can impair liver and kidney function and significantly raise the risk of lactic acidosis when combined with metformin. Reducing or stopping alcohol use may make you eligible in the future.</p>
                <p>If you’re interested in exploring alternatives, our <a href="/heart">Heart Health Program</a> offers a comprehensive review of your condition and create a plan that works for your cardiometabolic needs. Our physician-led team will help you explore alternatives that are both effective and safe for you.</p>
              </>
              break
            case Disqualification.UNDER_18:
              text = <>Metformin isn’t typically used in people under 18. For most young people, the first step is building a foundation of healthy habits like better nutrition and regular physical activity. If your glucose or HbA1c remains very high, our <a href="/heart">Heart Health Program</a> offers expert guidance tailored for younger patients. A physician will work with you and your family to create a safe, long-term plan - whether that includes medication or not.</>
              break
            case Disqualification.HEART_FAILURE:
              text = <>We do not prescribe metformin if you have recent or ongoing episodes of decompensated heart failure. In these states, the body is at risk for hypoperfusion and poor oxygen delivery, which can elevate lactate levels and increase medication risk. Once your condition is stable and your heart function is well managed, metformin may be reconsidered with your cardiologist’s input.</>
              break
            case Disqualification.LOW_GLUCOSE:
              text = <>
              <p>We do not prescribe metformin if your fasting glucose is under 70 mg/dL, which is considered hypoglycemic. Starting metformin at this level could worsen low blood sugar, especially in people who are lean, athletic, or already insulin-sensitive.</p>
              <p>If you’re interested in exploring alternatives, our <a href="/heart">Heart Health Program</a> offers a comprehensive review of your condition and create a plan that works for your cardiometabolic needs. Our physician-led team will help you explore alternatives that are both effective and safe for you.</p>
              </>
              break
            case Disqualification.LOW_HBA1C:
              text = <>
              <p>An HbA1c less than 4.8% is low and suggests either naturally very low average glucose or potential overcorrection from diet/lifestyle changes. In these cases, we require physician oversight to ensure safety.</p>
              <p>If you’re interested in exploring metformin still or considering other alternatives, our <a href="/heart">Heart Health Program</a> offers a comprehensive review of your condition and create a plan that works for your cardiometabolic needs. Our physician-led team will help you explore alternatives that are both effective and safe for you.</p>
              </>
              break

            default:
              text = null
              break;
          }

          return <>
            {text ? <p>{text}</p> : <p>Based on your answers, we do not recommend ezetimibe at this time. Please contact us if you disagree or have any questions. We're happy to consider situations on a case-by-case basis.</p>}
            
          </>
        },
        buttonText: [ 
          Disqualification.METFORMIN_ADVERSE_REACTION, 
          Disqualification.END_STAGE_KIDNEY_DISEASE, 
          Disqualification.CHRONIC_KIDNEY_DISEASE,
          Disqualification.UNDER_18,
          Disqualification.HEAVY_ALCOHOL_CONSUMPTION,
          Disqualification.LOW_GLUCOSE,
          Disqualification.LOW_HBA1C,
        ].includes(disqualifications?.[0]) ? 'Go to Heart Health Program' : null,
        enterStyle: { display: 'none' },
        path: '/heart',
        buttonStyle: { display: [ 
          Disqualification.METFORMIN_ADVERSE_REACTION, 
          Disqualification.END_STAGE_KIDNEY_DISEASE, 
          Disqualification.CHRONIC_KIDNEY_DISEASE,
          Disqualification.UNDER_18,
          Disqualification.HEAVY_ALCOHOL_CONSUMPTION,
          Disqualification.LOW_GLUCOSE,
          Disqualification.LOW_HBA1C,
        ].includes(disqualifications?.[0])  ? '' : 'none' },

      },


    
      [Step.SELECT_DURATION]: {
        type: StepType.PRODUCT_SELECT,
        title: `How much Metformin (500 mg) would you like to order?`,
        description: "Pick the option that fits your goals. Each includes automatic refills. Cancellation is easy and available anytime before renewal.",
        nextStep: Step.DELIVERY,
        buildFlow: true,
        field: {
          name: 'membershipTypeCode',
          required: true,
          options: [
            {
              title: '30 tablets',
              value: MembershipTypeCode.METFORMIN_500MG_1MO,
              price: `$${getMembershipType(MembershipTypeCode.METFORMIN_500MG_1MO)?.cost}/month`,
              description: `Refilled & billed monthly at $${getMembershipType(MembershipTypeCode.METFORMIN_500MG_1MO)?.cost}. Best if just assessing tolerance.`,
            },
            {
              title: '90 tablets',
              value: MembershipTypeCode.METFORMIN_500MG_3MO,
              price: `$${getMembershipType(MembershipTypeCode.METFORMIN_500MG_3MO)?.cost/3}/month`,
              description: `Refilled & billed quarterly at $${getMembershipType(MembershipTypeCode.METFORMIN_500MG_3MO)?.cost}. Ideal for first-time users to evaluate response and initial labs.`,
            },
            {
              title: '365 tablets',
              value: MembershipTypeCode.METFORMIN_500MG_12MO,
              price: `$${getMembershipType(MembershipTypeCode.METFORMIN_500MG_12MO)?.cost/12}/month`,
              description: `Refilled & billed annually at $${getMembershipType(MembershipTypeCode.METFORMIN_500MG_12MO)?.cost}. Best value for long-term use.`,
            },

          ],
        },

      },

      [Step.DELIVERY]: {
        type: StepType.LOCATION,
        buildUser: true,
        nextStep: Step.PAY,
        title: `What's your delivery address?`,
        description: `We'll send your medication to this location.`,
        field: {
          name: 'shippingLocation'
        },
        
      },

      [Step.PAY]: {
        type: StepType.PAY,
        nextStep: Step.NAME,
        addSubscription: true,
        paymentTypes: ['card'],
        subscriptionType: flow?.membershipTypeCode,
        title: () => { 
          const selectedMembershipType = getMembershipType(flow?.membershipTypeCode);
          const totalCost = ProductHelper.getTotalCost([selectedMembershipType], instalabMembership, currentUser)
          
          const isAnnual = selectedMembershipType?.isAnnual
          const isQuarterly = selectedMembershipType?.isQuarterly
          const isMonthly = selectedMembershipType?.isMonthly
          
          return <>Pay <span className="true-price">${totalCost}</span> for your {isAnnual ? '365' : isQuarterly ? '90' : isMonthly ? '30' : ''}  tablets of {selectedMembershipType?.title}</>
        },
        description: () => {

          const selectedMembershipType = getMembershipType(flow?.membershipTypeCode);
          const isAnnual = selectedMembershipType?.isAnnual
          const isQuarterly = selectedMembershipType?.isQuarterly
          const isMonthly = selectedMembershipType?.isMonthly
          
          return <>You'll be refilled and billed {isAnnual ? 'annually' : isQuarterly ? 'every 3 months' : isMonthly ? 'monthly' : ''} this amount. If plans change, cancellation is very easy and always available before renewal.</>
        },
       
      },


      [Step.NAME]: {
        type: StepType.MULTIPLE_INPUT,
        nextStep: Step.CONTACT,
        buildUser: true,
        title: `What's your full name?`,
        description: `We need this for your prescription.`,
        fields: [{
          name: 'firstName',
          placeholder: 'First Name',
          required: true,
        }, {
          name: 'lastName',
          placeholder: 'Last Name',
          required: true,
        }],
        skipIf: () => {
          return initialUser?.firstName && initialUser?.lastName
        }
      },

      [Step.CONTACT]: {
        type: StepType.MULTIPLE_FIELDS,
        nextStep: Step.GOV_ID,
        buildUser: true,
        title: `How can we reach you?`,
        description: "We’ll send your tracking information for your medication here.",
        fields: [
          {
          name: 'email',
          label: 'Email Address',
          required: true,
          type: 'email',
          unique: true,
        }, {
          name: 'phone',
          label: 'Phone Number',
          required: true,
          type: 'phone',
        }],
        skipIf: () => {
          return (initialUser?.email && initialUser?.phone)
        }
      },


      [Step.GOV_ID]: {
        type: StepType.INSURANCE_UPLOAD,
        buildUser: true,
        nextStep: Step.ADDITIONAL_INFO,
        title: 'Upload your government-issued ID',
        description: `To keep you safe and stay compliant with the law, we need to verify your identity before we can send your medication. Accepted IDs include a passport, driver’s license, or state ID.`,
        fields: [
          {
            name: 'govId',
            fileTypeCode: FileTypeCode.GOV_ID,
            upload: true,
            title: 'Upload your ID securely here',
            required: true,
          },
    
        ],
    
        skipIf: () => {
          return initialUser?.govId
        }
      },


      [Step.ADDITIONAL_INFO]: {
        type: StepType.MULTIPLE_FIELDS,
        nextStep: Step.PASSWORD,
        buildUser: true,
        buttonText: 'Finish',
        title: `One last step to finalize your order.`,
        description: `We just need a few last details to ensure we give you the right instructions on how to safely take your medication.`,
        fields: [

          {
            type: FieldType.TEXTAREA,
            name: 'freeformMedications',
            placeholder: 'Type medications here ...',
            label: <span className="highlight-label">List any medications you currently take.</span>,
          },   
          {
            type: FieldType.TEXTAREA,
            name: 'freeformSupplements',
            placeholder: 'Type supplements here ...',
            label: <span className="highlight-label">List any vitamins or supplements you currently take.</span>,
          },     
  

        ],
      },

      

      [Step.PASSWORD]: {
        type: StepType.SINGLE_INPUT,
        nextStep: Step.CONFIRM,
        buildUser: true,
        title: `Finally, create a password for your Instalab account.`,
        description: `This lets you securely access your prescription and track your health over time.`,
        field: {
          name: 'password',
          placeholder: 'Enter your password',
          required: true,
          password: true,
        },
        skipIf: () => {
          return hasPass
        },
        onLoad: async () => {
          if (flow?._id && !hasCompletedFlowRef.current) {
            hasCompletedFlowRef.current = true; // Mark flow as completed
            await completeFlow(flow._id);
          }
        }
      },

  
      [Step.CONFIRM]: {
        type: StepType.STATIC,
        title: `All set! `,
        path: `/flow/${FlowType.INTAKE}`,
        description: <>We'll email you in 1 business day with tracking information for your medication. If you have some time, please hit 'Continue' to tell us more about yourself so we can help make better recommendations on your health to you.</>,
        buttonText: 'Continue',
        content: <MetforminConfirm flow={flow} />,
        onLoad: async () => {
          if (flow?._id && !hasCompletedFlowRef.current) {
            hasCompletedFlowRef.current = true; // Mark flow as completed
            await completeFlow(flow._id);
          }
        },

      },
    })
  }


  return (
    <Flow 
      skeleton={skeleton} 
      flow={flow} 
      setFlow={setFlow}
      initialUser={initialUser}
      setInitialUser={setInitialUser}
      setHasPass={setHasPass}
    />
  )
}